import {
  Component,
  EventEmitter,
  HostListener,
  OnDestroy,
  OnInit,
  Output,
  inject,
  signal,
} from '@angular/core';
import { DatePipe, DecimalPipe } from '@angular/common';
import { LucideAngularModule, KeyRound, RefreshCw, X } from 'lucide-angular';
import { SupervisorService } from '@core/services';
import type { SupervisorCheck, SupervisorOutcome } from '@core/models';

/** How each answer reads, and how it looks. */
const OUTCOME: Record<SupervisorOutcome, { label: string; classes: string }> = {
  approved: { label: 'Aprobada', classes: 'bg-emerald-50 text-emerald-700' },
  denied: { label: 'Denegada', classes: 'bg-red-50 text-red-700' },
  bad_code: { label: 'Código incorrecto', classes: 'bg-amber-50 text-amber-700' },
};

/**
 * Regente — the four digits, and what has been asked with them.
 *
 * A dialog opened from the profile block of the sidebar, not a module: it is a
 * thing the regente reaches for mid-shift, wherever they are (user,
 * 2026-10-15). Mounted only while open, so every opening asks the backend for
 * the code afresh.
 *
 * The code is not a password and not a secret worth guarding on this screen: it
 * is what the regente dictates at the counter, and it expires on the hour. What
 * this dialog owes them is that the number on screen is the number the backend
 * will accept — so it asks for it again rather than counting down to one it
 * computed itself.
 *
 * And when the window turns, the number goes away and a button takes its place
 * (user, 2026-09-24). It does NOT refresh itself: a tab that was asleep or in
 * the background gets its timers throttled, so an automatic refresh arrives
 * late and the screen would keep showing a number it can no longer vouch for.
 * A screen that says "press here" is never wrong; one that quietly shows a dead
 * code makes the regente look like the problem. Pressing costs a second, once
 * an hour at most — and the code that just expired is still accepted for the
 * next window, so nobody is left stranded mid-sentence either way.
 *
 * Under it, what THEY have answered — not a colleague's at the same branch,
 * and not the wrong codes, which are nobody's (user, 2026-10-15; the backend
 * filters it). Who answered what across a branch is in each dispensation's
 * supervision section.
 */
@Component({
  selector: 'app-supervisor-dialog',
  imports: [LucideAngularModule, DatePipe, DecimalPipe],
  templateUrl: './supervisor-dialog.component.html',
})
export class SupervisorDialogComponent implements OnInit, OnDestroy {
  @Output() readonly close = new EventEmitter<void>();

  private readonly _supervisor = inject(SupervisorService);
  private _timer?: ReturnType<typeof setInterval>;

  protected readonly icons = { KeyRound, RefreshCw, X };
  protected readonly code = signal<string>('····');
  protected readonly secondsLeft = signal<number>(0);
  protected readonly checks = signal<SupervisorCheck[]>([]);
  /** The window turned: what is on screen is no longer this hour's code. */
  protected readonly stale = signal(false);
  protected readonly loading = signal(true);
  protected readonly failed = signal(false);

  async ngOnInit(): Promise<void> {
    await Promise.all([this.refreshCode(), this.refreshChecks()]);
    this.loading.set(false);
    // One tick a second: the countdown is the only moving part. At zero it
    // stops and the code is put away — asking for the next one is the
    // regente's press, not ours.
    this._timer = setInterval(() => {
      const left = this.secondsLeft() - 1;
      this.secondsLeft.set(Math.max(0, left));
      if (left <= 0) this.stale.set(true);
    }, 1000);
  }

  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    this.close.emit();
  }

  ngOnDestroy(): void {
    if (this._timer) clearInterval(this._timer);
  }

  protected async refreshCode(): Promise<void> {
    try {
      const answer = await this._supervisor.code();
      this.code.set(answer.code);
      this.secondsLeft.set(answer.expires_in);
      this.stale.set(false);
      this.failed.set(false);
    } catch {
      this.failed.set(true);
      this.stale.set(true);
    }
  }

  protected async refreshChecks(): Promise<void> {
    try {
      this.checks.set((await this._supervisor.checks()).results);
    } catch {
      this.checks.set([]);
    }
  }

  /** "58:12" — what is left of this code. */
  protected get countdown(): string {
    const left = Math.max(0, this.secondsLeft());
    const minutes = Math.floor(left / 60);
    return `${minutes}:${String(left % 60).padStart(2, '0')}`;
  }

  protected outcome(check: SupervisorCheck) {
    return (
      OUTCOME[check.outcome] ?? { label: check.outcome_label, classes: 'bg-gray-100 text-gray-600' }
    );
  }

  protected source(check: SupervisorCheck): string {
    return check.price_source === 'erp'
      ? 'precio del ERP'
      : check.price_source === 'catalog'
        ? 'precio del catálogo'
        : '';
  }
}
